import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { styled } from 'goober';

import { useAccountSwitchNotificationFromUrl } from '@/features/account/hooks/use-account-switch-notification-from-url';

import { OverviewPageContainer } from '@/components/layout/overview-page-container';
import { trackProjectOpened } from '@/lib/mixpanel/mixpanel';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { ParagraphSmall } from '@/ui/typography/widgets';
import { WithHtmlSavvyTitle } from '@/with-html-savvy-title';

import { ActionPanel } from '../action-panel/action-panel';
import { useActionPanel } from '../action-panel/use-action-panel';

import { useProject } from './overview/use-project.query';
import { listingSidebarIdState, projectIdState } from './project.state';
import { useActionPanelSearchParams } from '../action-panel/use-action-panel-search-params';
import { ProjectSidebar } from './overview/sidebar/project-sidebar';
import { useProjectSidebar } from './overview/sidebar/use-project-sidebar';
import { ProjectListings } from './project-listings/project-listings';
import { generateProjectCreatedBy } from './overview/helpers';
import { ProjectHeader } from './overview/header/project-header';
import { useUpdateProject } from './overview/use-update-project.mutation';
import {
  useProjectHeaderForm,
  type ProjectHeaderForm,
} from './overview/header/edit/use-project-header-form';
import { getEditFormDefaultValues } from './overview/header/helpers';
import { FormProvider } from 'react-hook-form';
import { ProjectListingSidebar } from './project-listings/project-listing-sidebar/project-listing-sidebar';
import { useAtom } from 'jotai';
import { NotificationsSidebar } from '../notifications/notifications-sidebar';

export const ProjectOverviewPage = () => {
  const { projectId: projectIdParam } = useParams();
  const { t } = useTranslation('projects');
  useAccountSwitchNotificationFromUrl();
  const { closeActionPanel, openActionPanelOnSearchParamTab } =
    useActionPanel();
  const [projectId, setProjectId] = useAtom(projectIdState);
  const { data: fetchedProject, isLoading, isFetched } = useProject(projectId);
  const { actionPanelSearchParam } = useActionPanelSearchParams();
  const { isOpen: isProjectSidebarOpen, handleChangeToggleProjectSidebar } =
    useProjectSidebar();
  const [listingSidebarId, setListingSidebarId] = useAtom(
    listingSidebarIdState,
  );
  const { editProject } = useUpdateProject();
  const methods = useProjectHeaderForm(
    getEditFormDefaultValues(fetchedProject),
  );
  const { getFieldState } = methods;
  const lastTrackedProjectOpenIdRef = useRef<string | null>(null);

  useEffect(() => {
    lastTrackedProjectOpenIdRef.current = null;
  }, [projectIdParam]);

  useEffect(() => {
    if (
      !projectIdParam ||
      !fetchedProject?.id ||
      fetchedProject.id !== projectIdParam ||
      !fetchedProject.name
    ) {
      return;
    }
    if (lastTrackedProjectOpenIdRef.current === projectIdParam) {
      return;
    }
    lastTrackedProjectOpenIdRef.current = projectIdParam;
    trackProjectOpened({
      project_id: fetchedProject.id,
      project_name: fetchedProject.name,
    });
  }, [fetchedProject?.id, fetchedProject?.name, projectIdParam]);

  const openedDeepLinkKeyRef = useRef<string | null>(null);

  useEffect(() => {
    openedDeepLinkKeyRef.current = null;
    setProjectId(projectIdParam ?? null);
    setListingSidebarId(null);

    return () => {
      closeActionPanel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectIdParam]);

  useEffect(() => {
    if (!actionPanelSearchParam?.id || !isFetched) {
      return;
    }

    const key = `${projectIdParam}:${actionPanelSearchParam.type}:${actionPanelSearchParam.id}`;

    if (openedDeepLinkKeyRef.current === key) {
      return;
    }

    openActionPanelOnSearchParamTab(actionPanelSearchParam, {
      context: 'project',
      title: fetchedProject?.name ?? '',
    });
    openedDeepLinkKeyRef.current = key;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionPanelSearchParam, fetchedProject?.name, isFetched, projectIdParam]);

  const handleSaveChanges = async (values: ProjectHeaderForm) => {
    if (!projectId) {
      return;
    }

    const { isDirty: isNameDirty } = getFieldState('name');
    const { isDirty: isCreatedForDirty } = getFieldState('createdFor');
    const { isDirty: isStatusDirty } = getFieldState('status');
    const { isDirty: isTimeframeDirty } = getFieldState('timeframe');
    const { isDirty: isOwnerDirty } = getFieldState('owner');
    const { isDirty: isTypeDirty } = getFieldState('type');

    const { name, createdFor, status, timeframe, owner, type } = values;

    const payload = {
      projectId,
      ...(isNameDirty && name && { name }),
      ...(isCreatedForDirty && {
        teamId: createdFor?.value ?? null,
      }),
      ...(isTypeDirty && {
        typeId: type?.value ?? null,
      }),
      ...(isOwnerDirty &&
        owner?.value && {
          ownerId: Number(owner?.value),
        }),
      ...(isStatusDirty && { status }),
      ...(isTimeframeDirty &&
        timeframe && {
          ...timeframe,
        }),
    };

    try {
      await editProject(payload);
    } catch {
      // The mutation already surfaced the error. Reset the header form to the
      // saved project so the header doesn't keep showing an unsaved value
      // (e.g. a rejected duplicate name) after edit mode closes.
      methods.reset(getEditFormDefaultValues(fetchedProject));
    }
  };

  return (
    <WithHtmlSavvyTitle
      title={
        fetchedProject
          ? t(`projectOverview.page.title`, {
              projectName: fetchedProject.name,
            })
          : t`projects.project`
      }
    >
      <OverviewPageContainer
        name="project-page-container"
        direction="column"
        justify="space-between"
      >
        <Main name="project-page-main-content" direction="column">
          <FormProvider {...methods}>
            <ProjectHeader onEditSubmit={handleSaveChanges} />
          </FormProvider>
          <ProjectListings isProjectLoading={!!isLoading} />
        </Main>
        <Footer>
          <ParagraphSmall>
            {fetchedProject && generateProjectCreatedBy(fetchedProject)}
          </ParagraphSmall>
        </Footer>
      </OverviewPageContainer>

      {isProjectSidebarOpen && (
        <ProjectSidebar
          projectId={projectIdParam ?? null}
          onClose={handleChangeToggleProjectSidebar}
        />
      )}

      {!!listingSidebarId && (
        <ProjectListingSidebar
          listingId={listingSidebarId}
          onClose={() => setListingSidebarId(null)}
        />
      )}
      <ActionPanel />
      <NotificationsSidebar />
    </WithHtmlSavvyTitle>
  );
};

const Main = styled(Flexbox)`
  flex-grow: 1;
`;

const Footer = styled('div')`
  padding-top: 40px;
`;
